import React, { useState, useEffect, useRef } from 'react';
import { FaMusic, FaPaperPlane, FaRobot, FaVolumeUp, FaStop, FaPlay, FaPause } from 'react-icons/fa';

interface BookSegment {
    Content: string;
    AudioContent: string;
    SequenceIndex: number;
}

interface Message {
    role: 'user' | 'assistant';
    text: string;
}

const AudiobookPage: React.FC = () => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loginData, setLoginData] = useState({ username: '', password: '' });
    const [loginError, setLoginError] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const [segments, setSegments] = useState<BookSegment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [cursorInfo, setCursorInfo] = useState<{ index: number, char: number } | null>(null);
    const [cursorCoords, setCursorCoords] = useState<{ x: number, y: number, height: number } | null>(null);
    const [showHelp, setShowHelp] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        { role: 'assistant', text: 'Yapay zeka asistanı hazır. Mor imleci istediğiniz noktaya getirip tıkladıktan sonra sorunuzu sorabilirsiniz.' }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isAsking, setIsAsking] = useState(false);
    const [isReadingBook, setIsReadingBook] = useState(false);
    const chatScrollRef = useRef<HTMLDivElement>(null);
    const bookScrollRef = useRef<HTMLDivElement>(null);
    const textContainerRef = useRef<HTMLDivElement>(null);

    const BACKEND_URL = "https://audiobook.api.hckapps.com";

    useEffect(() => {
        const token = localStorage.getItem('audiobook_token');
        if (token) {
            setIsLoggedIn(true);
            setShowHelp(true);
        }
    }, []);

    useEffect(() => {
        if (!isLoggedIn) return;

        fetch('/lotr-tr.json')
            .then(res => res.json())
            .then(data => {
                // Start from sequence index 68 as requested
                const filteredData = data.filter((s: BookSegment) => s.SequenceIndex >= 68);
                setSegments(filteredData);
                setIsLoading(false);
            })
            .catch(err => {
                console.error("Veri yükleme hatası:", err);
                setIsLoading(false);
            });
    }, [isLoggedIn]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoginError('');
        setIsLoggingIn(true);

        try {
            const response = await fetch(`${BACKEND_URL}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginData)
            });

            const result = await response.json();

            if (result.success && result.token) {
                localStorage.setItem('audiobook_token', result.token);
                setIsLoggedIn(true);
                setShowHelp(true);
            } else {
                setLoginError(result.message || 'Giriş başarısız. Lütfen bilgileri kontrol edin.');
            }
        } catch (err) {
            setLoginError('Sunucuya bağlanılamadı. Lütfen internetinizi veya sunucu durumunu kontrol edin.');
        } finally {
            setIsLoggingIn(false);
        }
    };

    const updateCursorPosition = () => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const textContainer = textContainerRef.current;

        if (rect && textContainer) {
            const containerRect = textContainer.getBoundingClientRect();
            setCursorCoords({
                x: rect.left - containerRect.left,
                y: rect.top - containerRect.top,
                height: rect.height || 26
            });
        }

        const node = range.startContainer;
        let parent = node.parentElement;
        while (parent && !parent.getAttribute('data-index')) {
            parent = parent.parentElement;
        }

        if (parent) {
            const index = parseInt(parent.getAttribute('data-index') || '0');
            const charOffset = range.startOffset;
            setCursorInfo({ index, char: charOffset });
        }
    };

    const moveLaserToChar = (index: number, charOffset: number) => {
        const textContainer = textContainerRef.current;
        const segmentEl = textContainer?.querySelector(`[data-index="${index}"]`);
        
        if (segmentEl && textContainer) {
            const textNode = segmentEl.firstChild;
            if (!textNode) return;

            try {
                const range = document.createRange();
                const textLength = textNode.textContent?.length || 0;
                const safeOffset = Math.min(charOffset, textLength);
                
                range.setStart(textNode, safeOffset);
                range.setEnd(textNode, Math.min(safeOffset + 1, textLength));
                
                const rect = range.getBoundingClientRect();
                const containerRect = textContainer.getBoundingClientRect();
                
                if (rect.left !== 0) { // Check if rect is valid
                    setCursorCoords({
                        x: rect.left - containerRect.left,
                        y: rect.top - containerRect.top,
                        height: rect.height || 26
                    });
                }

                // If it's a new segment or we're at the start, scroll into view
                if (charOffset === 0) {
                    segmentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                
                setCursorInfo({ index, char: safeOffset });
            } catch (e) {
                // Fallback to segment start if range fails
                const rect = segmentEl.getBoundingClientRect();
                const containerRect = textContainer.getBoundingClientRect();
                setCursorCoords({
                    x: rect.left - containerRect.left,
                    y: rect.top - containerRect.top,
                    height: rect.height || 26
                });
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim() || !isLoggedIn || isLoggingIn || isAsking) return;
        
        const question = inputValue;
        setInputValue('');

        if (!cursorInfo) {
            setMessages(prev => [...prev, 
                { role: 'user', text: question },
                { role: 'assistant', text: 'Lütfen önce hikayede herhangi bir yere tıklayın ve sorunuzu sorun. O tıkladığınız yerden önceki bilgileri kullanarak sorunuzu cevaplayacağım.' }
            ]);
            return;
        }

        setIsAsking(true);
        setMessages(prev => [...prev, { role: 'user', text: question }]);
        
        // Prepare Context (Last 600 characters before the cursor)
        let context = "";
        if (cursorInfo) {
            const currentSegIdx = segments.findIndex(s => s.SequenceIndex === cursorInfo.index);
            if (currentSegIdx !== -1) {
                // Get text from current segment before cursor
                context = segments[currentSegIdx].AudioContent.substring(0, cursorInfo.char);
                
                // If not enough, go backwards
                let lookback = currentSegIdx - 1;
                while (context.length < 600 && lookback >= 0) {
                    context = segments[lookback].AudioContent + context;
                    lookback--;
                }
                context = context.slice(-600); // Keep only last 600
            }
        }

        try {
            const token = localStorage.getItem('audiobook_token');
            const params = new URLSearchParams({
                q: question,
                bookid: "4",
                seq: cursorInfo ? cursorInfo.index.toString() : "0",
                context: context
            });

            const response = await fetch(`${BACKEND_URL}/api/ask?${params.toString()}`, {
                method: 'GET',
                headers: { 
                    'Authorization': token || '',
                    'Content-Type': 'application/json' 
                }
            });

            const result = await response.json();
            
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                text: result.answer || 'Üzgünüm, şu an cevap veremiyorum.' 
            }]);
        } catch (err) {
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                text: 'Sistemsel bir hata oluştu. Lütfen bağlantınızı kontrol edin.' 
            }]);
        } finally {
            setIsAsking(false);
        }
    };

    const speakText = (text: string) => {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'tr-TR';
        utterance.rate = 0.8;
        utterance.pitch = 0.9;
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeaking = () => {
        window.speechSynthesis.cancel();
        setIsReadingBook(false);
    };

    const readBookFromCursor = () => {
        if (segments.length === 0) return;
        
        // Ensure any previous speech is cancelled before starting a new one
        window.speechSynthesis.cancel();
        
        setIsReadingBook(true);
        let startIdx = 0;
        let charStart = 0;

        if (cursorInfo) {
            startIdx = segments.findIndex(s => s.SequenceIndex === cursorInfo.index);
            charStart = cursorInfo.char;
            if (startIdx === -1) {
                startIdx = 0;
                charStart = 0;
            }
        }

        readSegmentRecursive(startIdx, charStart);
    };

    const readSegmentRecursive = (segIdx: number, charStart: number = 0) => {
        if (segIdx >= segments.length) {
            setIsReadingBook(false);
            return;
        }

        const segment = segments[segIdx];
        
        const textToRead = segment.AudioContent.substring(charStart);
        const utterance = new SpeechSynthesisUtterance(textToRead);
        utterance.lang = 'tr-TR';
        utterance.rate = 0.8;
        utterance.pitch = 0.9;

        // Move laser word by word
        utterance.onboundary = (event) => {
            if (event.name === 'word') {
                moveLaserToChar(segment.SequenceIndex, charStart + event.charIndex);
            }
        };

        // Initial move for the segment
        moveLaserToChar(segment.SequenceIndex, charStart);

        utterance.onend = () => {
             // Use setTimeout to ensure we don't hit race conditions with state
             setTimeout(() => {
                if (window.speechSynthesis.speaking || window.speechSynthesis.pending) return;
                readSegmentRecursive(segIdx + 1, 0);
             }, 100);
        };
        
        utterance.onerror = () => {
            setIsReadingBook(false);
        };

        window.speechSynthesis.speak(utterance);
    };

    useEffect(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [messages]);

    if (!isLoggedIn) {
        return (
            <div className="h-screen w-full bg-[#050505] flex items-center justify-center font-sans p-6 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(168,85,247,0.08)_0%,_transparent_70%)] pointer-events-none" />
                <div className="max-w-md w-full animate-fade-in relative z-10">
                    <div className="text-center mb-12">
                        <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-[2rem] flex items-center justify-center shadow-2xl shadow-purple-500/20 mx-auto mb-6 transform rotate-12 group">
                             <FaMusic className="text-white text-3xl" />
                        </div>
                        <h1 className="text-4xl font-black tracking-tighter uppercase tracking-[0.3em] text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500 font-mono">AUDIOBOOK AI</h1>
                        <p className="text-gray-500 mt-4 font-medium uppercase tracking-widest text-xs">Erişim için yetkilendirme gerekiyor</p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div className="relative group">
                            <input 
                                type="text"
                                value={loginData.username}
                                onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                                placeholder="Kullanıcı Adı"
                                className="w-full bg-white/[0.03] border border-white/10 rounded-2xl py-6 px-8 text-white focus:outline-none focus:border-purple-500/50 transition-all placeholder:text-gray-700"
                                required
                            />
                        </div>
                        <div className="relative group">
                            <input 
                                type="password"
                                value={loginData.password}
                                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                                placeholder="Şifre"
                                className="w-full bg-white/[0.03] border border-white/10 rounded-2xl py-6 px-8 text-white focus:outline-none focus:border-purple-500/50 transition-all placeholder:text-gray-700"
                                required
                            />
                        </div>

                        {loginError && (
                            <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-sm py-4 px-6 rounded-2xl animate-shake">
                                {loginError}
                            </div>
                        )}

                        <button 
                            type="submit"
                            disabled={isLoggingIn}
                            className="w-full py-6 bg-purple-600 hover:bg-purple-500 text-white font-black uppercase tracking-[0.2em] rounded-2xl transition-all shadow-xl shadow-purple-900/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed group"
                        >
                            {isLoggingIn ? (
                                <div className="flex items-center justify-center gap-3">
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>GİRİŞ YAPILIYOR</span>
                                </div>
                            ) : (
                                "SİSTEME GİRİŞ YAP"
                            )}
                        </button>
                    </form>
                    <p className="text-center text-[10px] text-gray-700 mt-10 font-bold uppercase tracking-[0.3em] opacity-40">AUDIOBOOK AI VERI KORUMA SISTEMI V1.0</p>
                </div>
            </div>
        );
    }

    return (
        <div lang="tr" className="h-screen w-full bg-[#050505] text-white flex flex-col overflow-hidden font-sans border-t border-purple-500/20">
            <nav className="h-16 md:h-20 shrink-0 flex items-center justify-between px-4 md:px-10 bg-black/40 backdrop-blur-xl border-b border-white/5 z-50">
                <div className="w-1/3 md:w-1/4 flex items-center gap-2 md:gap-4">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20 shrink-0">
                        <FaMusic className="text-white text-sm md:text-lg" />
                    </div>
                    <div>
                        <h1 className="text-sm md:text-xl font-black tracking-tight leading-none uppercase md:tracking-[0.2em] text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 font-mono truncate">AUDIOBOOK AI</h1>
                    </div>
                </div>

                <div className="flex-grow flex justify-center items-center gap-4 md:gap-8">
                    {!isReadingBook ? (
                         <button 
                            onClick={readBookFromCursor}
                            className={`w-10 h-10 md:w-14 md:h-14 rounded-xl flex items-center justify-center transition-all border border-white/10 bg-white/5 hover:bg-purple-600/20 text-purple-400`}
                            title="Dinle"
                        >
                            <FaPlay className="text-xs md:text-xl" />
                        </button>
                    ) : (
                        <button 
                            onClick={stopSpeaking}
                            className="w-10 h-10 md:w-14 md:h-14 bg-red-600/20 border border-red-500/40 rounded-xl flex items-center justify-center transition-all text-red-500 hover:bg-red-600/40"
                            title="Durdur"
                        >
                            <FaPause className="text-xs md:text-xl" />
                        </button>
                    )}
                </div>
                
                <div className="w-1/3 md:w-1/4 flex items-center justify-end gap-2 md:gap-6">
                    {cursorInfo && (
                        <div className="bg-transparent border border-purple-500/40 px-3 md:px-5 py-1.5 md:py-2 rounded-xl md:rounded-2xl flex items-center gap-2 md:gap-3 animate-fade-in shadow-[0_0_15px_rgba(168,85,247,0.1)]">
                            <span className="text-[8px] md:text-[9px] uppercase font-black text-purple-400 tracking-[0.2em] hidden sm:inline">IMLEÇ:</span>
                            <span className="text-xs md:text-lg font-mono font-bold text-white tracking-widest leading-none">[{cursorInfo.index}:{cursorInfo.char}]</span>
                        </div>
                    )}
                </div>
            </nav>

            {showHelp && (
                <div 
                    className="fixed inset-0 z-[100] bg-black/98 backdrop-blur-3xl overflow-y-auto overscroll-contain"
                    onClick={() => setShowHelp(false)}
                >
                    <div className="flex min-h-screen w-full items-start justify-center p-4 md:p-10 pointer-events-none">
                        <div 
                            className="max-w-5xl w-full bg-[#0a0a0a] border border-purple-500/20 rounded-[1.5rem] md:rounded-[2.5rem] p-6 md:p-12 shadow-[0_40px_100px_rgba(0,0,0,0.8)] animate-fade-in relative pointer-events-auto my-auto"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-10">
                                <h2 className="text-xl md:text-3xl font-black uppercase tracking-widest text-white leading-tight">AUDIOBOOK AI SİSTEMİ</h2>
                                <button 
                                    onClick={() => setShowHelp(false)}
                                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                                >
                                    ✕
                                </button>
                            </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
                            <div className="space-y-6">
                                <h3 className="text-[10px] font-black uppercase tracking-[0.4em] text-purple-500">NEDİR?</h3>
                                <div className="space-y-4 text-gray-400 text-sm md:text-lg leading-relaxed">
                                    <p className="text-white font-bold italic underline decoration-purple-500 underline-offset-8">Bu spoiler vermeyen okuma ve dinlemeye yardımcı olan bir yapay zeka asistanıdır.</p>
                                    <p>Yüzük kardeşliği Kitabı için hazırlanmıştır.</p>
                                    <p>Bu projenin asıl odak noktası yapay zeka asistanı deneyimidir; bu nedenle sesli okuma tarafında sistemin varsayılan (default) sesleri kullanılmıştır.</p>
                                </div>
                            </div>
                            <div className="space-y-6">
                                <h3 className="text-[10px] font-black uppercase tracking-[0.4em] text-purple-500">NASIL KULLANILIR?</h3>
                                <div className="space-y-4 text-gray-400 text-sm md:text-lg leading-relaxed">
                                    <p>İmleci istediğiniz bir noktaya getirip tıklayın ve soru sorun; ai size spoiler vermeden imleçten önceki bilgileri kullanarak cevap verecektir.</p>
                                    <p>İsterseniz imleci kitabın en sonuna getirip en başından bir şey sormayı deneyebilirsiniz.</p>
                                    <p>Şuanlık geçmiş konuşmaları hatırlamıyor, her sorunuz öncekilerden bağımsız olarak değerlendiriliyor.</p>
                                </div>
                            </div>
                            <div className="space-y-6">
                                <h3 className="text-[10px] font-black uppercase tracking-[0.4em] text-purple-500">NEDEN YAPTIM?</h3>
                                <div className="space-y-4 text-gray-400 text-sm md:text-lg leading-relaxed italic border-l border-purple-500/30 pl-6">
                                    <p>Dune fragmanı çıktıktan sonra Yüzüklerin Efendisi ve Dune gibi serilerin kitaplarını dinlemeye karar verdim ama çok karmaşık isimler, durumlar ve hikayeler vardı. Birini kaçırınca kitabın devamı mantıklı gelmiyordu.</p>
                                    <p>İşte tam da bu yüzden bu AI asistanını geliştirdim; kitabın neresinde olursam olayım, asistan sadece o ana kadar dinlediğim kısımları analiz ederek sorularıma spoiler vermeden cevap veriyor.</p>
                                </div>
                            </div>
                        </div>

                        <button 
                            onClick={() => setShowHelp(false)}
                            className="mt-12 w-full py-5 bg-purple-600 hover:bg-purple-700 rounded-xl font-black uppercase tracking-widest text-[13px] transition-all text-white border border-white/10"
                        >
                            SİSTEMİ KULLANMAYA BAŞLA
                        </button>
                    </div>
                </div>
            </div>
        )}



            <div className="flex-grow flex flex-col md:flex-row min-h-0 relative">
                <div className="flex-[3] flex flex-col min-h-0 bg-[#070707] selection:bg-purple-600 selection:text-white">
                    <div 
                        ref={bookScrollRef}
                        contentEditable={!isLoading}
                        suppressContentEditableWarning={true}
                        spellCheck="false"
                        onKeyUp={updateCursorPosition}
                        onMouseUp={updateCursorPosition}
                        onKeyDown={handleKeyDown}
                        className="flex-grow overflow-y-auto px-6 md:px-24 py-8 md:py-16 scroll-smooth custom-sidebar cursor-text outline-none caret-transparent"
                        lang="tr"
                    >
                        <div ref={textContainerRef} className="max-w-5xl mx-auto relative pt-4 md:pt-10">
                            <div className="mb-8 md:mb-16 border-l-4 border-purple-500 pl-4 md:pl-8 pointer-events-none select-none">
                                <h2 className="text-2xl md:text-4xl font-black uppercase tracking-[0.2em] mb-2 leading-tight">Yüzük Kardeşliği</h2>
                                <p className="text-gray-500 text-[10px] md:text-sm font-bold tracking-widest uppercase">The Fellowship of the Ring</p>
                            </div>

                            {cursorCoords && (
                                <div 
                                    className="absolute bg-purple-500 w-[3px] shadow-[0_0_10px_#a855f7,0_0_20px_#a855f7] animate-[laser_0.7s_infinite] pointer-events-none z-20"
                                    style={{ 
                                        left: cursorCoords.x, 
                                        top: cursorCoords.y, 
                                        height: cursorCoords.height 
                                    }}
                                />
                            )}

                            {isLoading ? (
                                <div className="flex items-center justify-center py-20">
                                    <div className="w-16 h-[2px] bg-purple-500 animate-pulse" />
                                </div>
                            ) : (
                                <article className="prose prose-invert prose-lg max-w-none">
                                    <div className="text-gray-400 font-serif space-y-8 text-xl md:text-2xl leading-[1.9] tracking-normal selection:bg-purple-600/50">
                                        {segments.map((seg, idx) => (
                                            <span 
                                                key={idx} 
                                                data-index={seg.SequenceIndex}
                                                className="mr-1.5 transition-all duration-300"
                                            >
                                                {seg.AudioContent}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="h-60" />
                                </article>
                            )}
                        </div>
                    </div>
                </div>

                <div className="hidden md:block w-px bg-white/5 h-full" />

                <aside className="flex-[2] flex flex-col min-h-0 bg-black/60 backdrop-blur-[60px] border-l border-white/5">
                    <div className="p-8 border-b border-white/5 bg-white/[0.02] shrink-0">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="h-2 w-2 rounded-full bg-purple-500/40" />
                                <h3 className="font-bold uppercase tracking-widest text-[11px] text-gray-400">AUDIO ASSISTANT</h3>
                            </div>
                            {cursorInfo && (
                                <div className="text-[10px] font-mono font-bold bg-purple-600/10 text-purple-400 px-3 py-1 rounded-full uppercase border border-purple-500/20">
                                    LOC: {cursorInfo.index}:{cursorInfo.char}
                                </div>
                            )}
                        </div>
                    </div>

                    <div 
                        ref={chatScrollRef}
                        className="flex-grow overflow-y-auto p-4 md:p-8 space-y-6 md:space-y-8 custom-sidebar"
                    >
                        {messages.map((msg, i) => (
                            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                                <div className={`max-w-[90%] md:max-w-[85%] group`}>
                                    <div className={`p-4 md:p-6 rounded-2xl md:rounded-3xl text-sm md:text-[15px] leading-relaxed transition-all ${
                                        msg.role === 'user' 
                                            ? 'bg-purple-600/20 border border-purple-500/30 text-white shadow-lg shadow-purple-900/10 rounded-tr-none' 
                                            : 'bg-white/[0.03] border border-white/5 text-gray-300 rounded-tl-none group-hover:bg-white/[0.05]'
                                    }`}>
                                        {msg.text}
                                        {msg.role === 'assistant' && (
                                            <div className="mt-4 flex gap-2 border-t border-white/5 pt-3">
                                                <button 
                                                    onClick={() => speakText(msg.text)}
                                                    className="flex items-center gap-2 text-[10px] font-black uppercase tracking-tighter text-purple-400 hover:text-purple-300 transition-colors bg-purple-500/10 px-3 py-1.5 rounded-lg border border-purple-500/20"
                                                >
                                                    <FaVolumeUp className="text-xs" />
                                                    DİNLE
                                                </button>
                                                <button 
                                                    onClick={stopSpeaking}
                                                    className="flex items-center gap-2 text-[10px] font-black uppercase tracking-tighter text-gray-500 hover:text-red-400 transition-colors px-3 py-1.5 rounded-lg border border-white/5"
                                                >
                                                    <FaStop className="text-[8px]" />
                                                    DURDUR
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className={`text-[8px] md:text-[9px] uppercase tracking-widest font-black mt-2 text-gray-600 flex items-center gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        {msg.role === 'assistant' && <FaRobot className="text-purple-500" />}
                                        {msg.role === 'user' ? 'Gezgin' : 'AI Odak'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="p-6 bg-white/[0.02] border-t border-white/5">
                        <form onSubmit={handleSendMessage} className="relative group">
                            <input 
                                type="text" 
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                disabled={isAsking}
                                placeholder={isAsking ? "Asistan düşünüyor..." : "Bir detay veya karakter sor..."} 
                                className="w-full bg-white/[0.03] border border-white/10 rounded-2xl py-5 pl-7 pr-16 text-white text-[15px] focus:outline-none focus:border-purple-500/50 focus:bg-white/[0.05] transition-all placeholder:text-gray-600 disabled:opacity-50"
                            />
                            <button 
                                type="submit"
                                disabled={isAsking}
                                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-purple-600 hover:bg-purple-500 rounded-xl flex items-center justify-center transition-all shadow-lg shadow-purple-900/20 active:scale-95 disabled:opacity-50"
                            >
                                {isAsking ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <FaPaperPlane className="text-white text-xs" />
                                )}
                            </button>
                        </form>
                        <p className="mt-4 text-center text-[9px] text-gray-700 uppercase tracking-widest font-black">
                            Mor imlece göre gerçek zamanlı analiz
                        </p>
                    </div>
                </aside>
            </div>

            <style>{`
                .custom-sidebar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-sidebar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-sidebar::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 10px;
                }
                .custom-sidebar:hover::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.15);
                }
                @keyframes laser {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0; }
                }
                @keyframes loading {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                @media (max-width: 768px) {
                    .flex-grow.flex-col.md\\:flex-row { flex-direction: column !important; }
                    aside { height: 400px; flex: none !important; }
                }
            `}</style>
        </div>
    );
};

export default AudiobookPage;
